import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Icons,
  iconSize,
  Stack,
  StatusChip,
  TagChip,
  TextField,
  Tooltip,
  Typography,
} from '@munifocus/ui';

/**
 * One reviewable field from an extracted Service New Brunswick record.
 *
 * The officer sees three things at once: what the document said, what will be
 * saved, and whether those differ. That is the whole point — these values end up
 * in a statutory Notice to Comply, so the officer has to be able to overrule any
 * of them, and the record has to show that they did.
 *
 * Changing a value is always allowed and never warned about. The nudge is the
 * quiet "from the PAN record" source chip and a "changed" badge, not a
 * confirmation dialog: officers correct SNB's casing routinely, and a modal on
 * every edit would train them to dismiss it.
 */

export interface ExtractedFieldRowProps {
  label: string;
  /** Verbatim value from the document. `null` when the record had no value. */
  extracted: string | null;
  value: string;
  onChange: (value: string) => void;
  /** Which record produced it, for the source chip. */
  source?: 'snb_pan' | 'snb_parcel' | null;
  /** Set when the two records disagree about this field. */
  mismatch?: { panValue: string | null; parcelValue: string | null } | null;
  /** True when the value was derived rather than read (see `inferOwnerType`). */
  inferred?: boolean;
  multiline?: boolean;
  disabled?: boolean;
  helperText?: string;
  /** Validation message. Shown in place of `helperText` and marks the field. */
  error?: string;
  /** The text input, so a form can move focus here when it is invalid. */
  inputRef?: Ref<HTMLInputElement>;
}

export function ExtractedFieldRow({
  label,
  extracted,
  value,
  onChange,
  source = null,
  mismatch = null,
  inferred = false,
  multiline = false,
  disabled = false,
  helperText,
  error,
  inputRef,
}: ExtractedFieldRowProps) {
  const { t } = useTranslation();
  const changed = (extracted ?? '') !== value;
  const canRevert = changed && extracted !== null;

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack
        useFlexGap
        direction="row"
        spacing={1}
        sx={{ mb: 0.5, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>

        {source && (
          <TagChip
            label={t(
              source === 'snb_pan' ? 'gov.extraction.source.pan' : 'gov.extraction.source.parcel',
            )}
          />
        )}

        {inferred && (
          <Tooltip title={t('gov.extraction.field.inferredHint')}>
            <StatusChip status="warning" label={t('gov.extraction.field.inferred')} />
          </Tooltip>
        )}

        {changed && <StatusChip status="pending" label={t('gov.extraction.field.changed')} />}

        {mismatch && (
          <Tooltip
            title={t('gov.extraction.field.mismatchHint', {
              pan: mismatch.panValue ?? '—',
              parcel: mismatch.parcelValue ?? '—',
            })}
          >
            <StatusChip status="rejected" label={t('gov.extraction.field.mismatch')} />
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          value={value}
          disabled={disabled}
          multiline={multiline}
          minRows={multiline ? 2 : undefined}
          onChange={(event) => onChange(event.target.value)}
          error={Boolean(error)}
          helperText={error ?? helperText}
          inputRef={inputRef}
          // The label is rendered above with its chips, so don't repeat it here.
          slotProps={{ htmlInput: { 'aria-label': label } }}
        />
        {canRevert && (
          <Tooltip title={t('gov.extraction.field.revert')}>
            <IconButton
              size="small"
              aria-label={t('gov.extraction.field.revertAria', { field: label })}
              onClick={() => onChange(extracted ?? '')}
              sx={{ mt: 0.25 }}
            >
              <Icons.RotateCcw size={iconSize.md} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {changed && extracted !== null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('gov.extraction.field.wasExtracted', { value: extracted })}
        </Typography>
      )}
      {extracted === null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('gov.extraction.field.notOnRecord')}
        </Typography>
      )}
    </Box>
  );
}
